export type AuthCaptchaScene = 'signup' | 'signin' | 'password-change';

type CaptchaCredentialPayload = {
  captchaId?: string;
  captchaOutput?: string;
  lotNumber?: string;
  passToken?: string;
  genTime?: string;
  validate?: string;
};

export function normalizePhoneNumber(raw: string) {
  return raw.replace(/\D/g, '').slice(-11);
}

export function isValidChinaPhone(raw: string) {
  return /^1\d{10}$/.test(normalizePhoneNumber(raw));
}

export function resolveCooldownSeconds(expiresAt?: string) {
  if (!expiresAt) {
    return 60;
  }

  const msRemaining = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(msRemaining)) {
    return 60;
  }

  return Math.max(0, Math.ceil(msRemaining / 1000));
}

export function isStrongPassword(password: string) {
  const trimmed = password.trim();
  if (trimmed.length < 8 || trimmed.length > 64) {
    return false;
  }

  return /[A-Za-z]/.test(trimmed) && /\d/.test(trimmed) && /[^A-Za-z0-9]/.test(trimmed);
}

const AUTH_ERROR_CODE_MESSAGES: Record<string, string> = {
  password_not_set: '您尚未设定密码',
  token_invalid: '登录失败，请检查手机号、密码或验证码',
  token_expired: '登录已过期，请重新登录',
  token_revoked: '会话已失效，请重新登录',
  auth_disabled: '鉴权服务暂不可用',
};

export function resolveApiErrorMessage(
  payload: { error?: string | null; message?: string | null } | null | undefined,
  fallback: string,
): string {
  const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
  if (message) {
    return message;
  }

  const error = typeof payload?.error === 'string' ? payload.error.trim() : '';
  if (error && AUTH_ERROR_CODE_MESSAGES[error]) {
    return AUTH_ERROR_CODE_MESSAGES[error];
  }

  return error || fallback;
}

export function isUserNotRegisteredMessage(message: string): boolean {
  return /用户不存在|尚未注册|请先注册|未注册/.test(message);
}

export function isPasswordNotSetMessage(message: string): boolean {
  return /尚未设定密码|尚未设置密码|未设定密码|未设置密码/.test(message);
}

export function formatErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

export function buildCaptchaRequest(payload: CaptchaCredentialPayload, phone: string, scene: AuthCaptchaScene) {
  if (!payload.captchaId || !payload.captchaOutput || !payload.lotNumber || !payload.passToken || !payload.genTime) {
    throw new Error('图形验证码凭证不完整，请重试');
  }

  return {
    captchaId: payload.captchaId,
    captchaOutput: payload.captchaOutput,
    lotNumber: payload.lotNumber,
    passToken: payload.passToken,
    genTime: payload.genTime,
    phone,
    scene,
    validate: payload.validate,
  };
}